import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Link,
  Fab,
} from '@mui/material';
import { KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import contentService, { MenuCategory, MenuItem } from '../services/contentService';
import { translateWithCache } from '../services/translation';
import { getImageUrl } from '../services/api';

const MenuPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  type DisplayCategory = MenuCategory & { originalName: string; displayName: string };
  const [menuCategories, setMenuCategories] = useState<DisplayCategory[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('Especialidades del Chef');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const categoriesRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categories, items] = await Promise.all([
          contentService.getMenuCategories(),
          contentService.getMenuItems({ category: 'Especialidades del Chef' })
        ]);
        const lang = (i18n.language || 'es').split('-')[0];
        console.log(`[MenuPage] Current language: ${lang}, i18n.language: ${i18n.language}`);

        // Prepare categories with original and display names
        const displayCategories: DisplayCategory[] = categories.map((c) => ({
          ...c,
          originalName: c.name,
          // Use name_en if available and language is English, otherwise use Spanish name
          displayName: lang === 'en' && c.name_en ? c.name_en : c.name,
        }));

        // Use manual translations from database if available, otherwise keep Spanish
        const displayItems: MenuItem[] =
          lang === 'en'
            ? items.map((it) => ({
                ...it,
                name: it.name_en || it.name,
                description: it.description_en || it.description,
                category: it.category_en || it.category,
                options: it.options, // Options don't have translations yet
              }))
            : items;
        setMenuCategories(displayCategories);
        setMenuItems(displayItems);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(t('menu.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [i18n.language, t]);

  const handleCategoryChange = async (categoryName: string) => {
    // categoryName here should always be the original (Spanish) name used by the API
    setSelectedCategory(categoryName);
    console.log(`[MenuPage] Category changed to: ${categoryName}`);

    try {
      let items = await contentService.getMenuItems({ category: categoryName });
      const lang = (i18n.language || 'es').split('-')[0];
      console.log(`[MenuPage] Category change - Language: ${lang}, Items count: ${items.length}`);

      if (lang === 'en') {
        console.log(`[MenuPage] Using manual translations for ${items.length} items`);
        items = items.map((it) => ({
          ...it,
          name: it.name_en || it.name,
          description: it.description_en || it.description,
          category: it.category_en || it.category,
          options: it.options, // Options don't have translations yet
        }));
        console.log(`[MenuPage] Manual translation applied for category: ${categoryName}`);
      }
      setMenuItems(items);
      // Mobile-only: After items update, scroll to the items section to signal change
      if (isMobile) {
        const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const doScroll = () => {
          if (itemsRef.current) {
            const rect = itemsRef.current.getBoundingClientRect();
            const absoluteTop = rect.top + window.pageYOffset;
            const offset = 12; // small offset so the header doesn't overlap
            window.scrollTo({ top: absoluteTop - offset, behavior: prefersReduced ? 'auto' : 'smooth' });
            // Ensure the back-to-top becomes visible after programmatic scroll
            setShowBackToTop(true);
          }
        };
        // Allow DOM to paint new content before scrolling
        requestAnimationFrame(() => setTimeout(doScroll, 0));
      }
    } catch (err: any) {
      console.error('Error fetching menu items:', err);
      setError(t('menu.failedToLoadItems'));
    }
  };

  // Show/hide back-to-top (to categories) button on mobile as user scrolls
  useEffect(() => {
    if (!isMobile) {
      setShowBackToTop(false);
      return;
    }
    const onScroll = () => {
      let reveal = window.scrollY > 50;
      if (categoriesRef.current) {
        const catRect = categoriesRef.current.getBoundingClientRect();
        // If the categories section bottom is above the top of viewport, we're past it
        if (catRect.bottom < 0) reveal = true;
      }
      setShowBackToTop(reveal);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [isMobile]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box component="section" sx={{ position: 'relative', overflowX: 'hidden' }}>
      {/* Background coffee elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        opacity: 0.05,
        zIndex: -1,
        overflow: 'hidden',
      }}>
        <Box>
          <img 
            src="/images/bg-coffee-elements.svg" 
            alt="" 
            style={{ width: '800px', height: '100%' }}
          />
        </Box>
      </Box>

      {/* Background gradient */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(to bottom, transparent, #FFF8E5)',
        opacity: 0.6,
        zIndex: -1,
      }} />

      {/* Header */}
      <Container maxWidth="lg" sx={{ pt: 10, px: { xs: 3, sm: 0, md: 0 } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 8,
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex' }}>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Bebas Neue", sans-serif',
                  textTransform: 'uppercase',
                  fontSize: '3rem',
                  color: 'white',
                  backgroundColor: '#78350F',
                  px: 2.5,
                  py: 1,
                }}
              >
                {t('home.menu.title')}
              </Typography>
            </Box>
            <Box sx={{ backgroundColor: '#009F9A', px: 2.5, py: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Hey August", cursive',
                  fontSize: '1.875rem',
                  color: 'white',
                }}
              >
                {t('home.menu.subtitle')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Category Filter Buttons */}
      <Container maxWidth="lg" sx={{ mb: 6, px: { xs: 3, sm: 0, md: 0 } }} ref={categoriesRef}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          gap: 2,
          justifyContent: { xs: 'flex-start', sm: 'center' },
          alignItems: { xs: 'stretch', sm: 'center' },
        }}>
          {menuCategories.map((category) => (
            <Button
              key={category.id}
              onClick={() => handleCategoryChange(category.originalName)}
              variant={selectedCategory === category.originalName ? 'contained' : 'outlined'}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                boxSizing: 'border-box',
                backgroundColor: selectedCategory === category.originalName ? '#009F9A' : 'transparent',
                color: selectedCategory === category.originalName ? 'white' : '#009F9A',
                border: `2px solid #009F9A`,
                borderRadius: '8px',
                px: 4,
                py: 1,
                fontSize: '1.1rem',
                fontFamily: '"Bebas Neue", sans-serif',
                textTransform: 'none',
                fontWeight: 400,
                '&:hover': {
                  backgroundColor: selectedCategory === category.originalName ? '#009F9A' : '#009F9A',
                  color: 'white',
                  borderColor: '#009F9A',
                },
                '&:focus': {
                  backgroundColor: selectedCategory === category.originalName ? '#009F9A' : '#009F9A',
                  color: 'white',
                  borderColor: '#009F9A',
                },
              }}
            >
              {category.displayName}
            </Button>
          ))}
        </Box>
      </Container>

      {/* Menu Items */}
      <Container maxWidth="lg" sx={{ px: { xs: 3, sm: 0, md: 0 } }} ref={itemsRef}>
        {menuItems.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">
              {t('menu.noItemsAvailable')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {menuItems.map((item) => (
              <Grid item xs={12} md={6} key={item.id}>
                <Card sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}>
                  {item.image_url && (
                    <Box
                      component="img"
                      src={getImageUrl(item.image_url)}
                      alt={item.name}
                      sx={{
                        width: '100%',
                        height: 200,
                        objectFit: 'cover',
                        borderTopLeftRadius: '16px',
                        borderTopRightRadius: '16px',
                      }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography
                        variant="h4"
                        component="h3"
                        sx={{
                          fontFamily: '"Bebas Neue", sans-serif',
                          fontWeight: 400,
                          color: '#513628',
                          flex: 1,
                        }}
                      >
                        {item.name}
                      </Typography>
                      {item.price != null && (
                        <Typography
                          variant="h5"
                          sx={{
                            fontFamily: '"Bebas Neue", sans-serif',
                            fontWeight: 700,
                            color: '#F89C24',
                            ml: 2,
                          }}
                        >
                          ${item.price.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                        fontSize: '1.1rem',
                      }}
                    >
                      {item.description}
                    </Typography>
                    
                    {/* Display options if available */}
                    {item.has_options && item.options && item.options.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {t('menu.options')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {item.options.map((option) => (
                            <Box 
                              key={option.id} 
                              sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                p: 1,
                                borderBottom: '1px dashed rgba(0,0,0,0.1)'
                              }}
                            >
                              <Typography variant="body2">{option.name}</Typography>
                              <Typography variant="body2" sx={{ color: '#F89C24', fontWeight: 'bold' }}>
                                ${option.price.toFixed(2)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                    {item.is_featured && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor: '#F89C24',
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}
                        >
                          {t('menu.new')}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Club Elite Section */}
      <Container maxWidth="lg" sx={{ py: 20, px: { xs: 3, md: 0 } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          p: { xs: 3, md: 10 },
          backgroundColor: '#523629',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}>
          <Box>
            <Box
              component="img"
              src="/images/club-elite-logo.png"
              alt="Club Elite"
              sx={{
                backgroundColor: 'white',
                width: '300px',
                p: 2.5,
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: 'white',
                opacity: 0.6,
                mt: 3.5,
                fontSize: '0.875rem',
              }}
            >
              *Los puntos no pueden ser utilizados para el pago de promociones
            </Typography>
          </Box>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'flex-start', md: 'flex-end' },
            textAlign: { xs: 'left', md: 'right' },
          }}>
            <Typography
              variant="h4"
              sx={{
                color: 'white',
                fontSize: '3.75rem',
                fontFamily: '"Bebas Neue", sans-serif',
                mt: { xs: 1.5, md: 0 },
              }}
            >
              Club Elite
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontFamily: '"Hey August", cursive',
                fontSize: '3rem',
                color: '#F89C24',
              }}
            >
              Registrate y acumula puntos
            </Typography>
            <Box sx={{
              width: '80px',
              height: '2px',
              backgroundColor: 'white',
              my: 3,
              ml: { xs: 0, md: 'auto' },
            }} />
            <Typography
              variant="body1"
              sx={{
                color: 'white',
                fontSize: '1.25rem',
              }}
            >
              Por tu consumo en Tosto Coffee recibirás <br />
              puntos que podrás acumular y canjear
            </Typography>
            <Link
              href="https://www.tostocoffee.com/registro-club-elite"
              sx={{
                fontSize: '1.5rem',
                color: '#009F9A',
                fontWeight: 'bold',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 2,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'none',
                },
              }}
            >
              Registrarme
              <svg 
                width="35" 
                height="35" 
                fill="#009F9A" 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24"
              >
                <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"></path>
              </svg>
            </Link>
          </Box>
        </Box>
      </Container>
      {isMobile && showBackToTop && (
        <Fab
          color="primary"
          aria-label="Back to categories"
          onClick={() => {
            const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (categoriesRef.current) {
              const rect = categoriesRef.current.getBoundingClientRect();
              const absoluteTop = rect.top + window.pageYOffset;
              window.scrollTo({ top: absoluteTop, behavior: prefersReduced ? 'auto' : 'smooth' });
            } else {
              window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
            }
          }}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 20,
            zIndex: 2000,
            backgroundColor: '#009F9A',
            '&:hover': { backgroundColor: '#00827F' },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}
    </Box>
  );
};

export default MenuPage; 